import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Sparkles, Mic, Shield, Clock, ArrowRight, Calendar, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { DemoConfigForm } from "@/components/demo/DemoConfigForm";
import { VoiceCallInterface } from "@/components/demo/VoiceCallInterface";
import { PostCallReflection } from "@/components/demo/honest-demo";
import { DemoConfig } from "@/hooks/use-vapi-demo";
import { usePageMeta } from "@/hooks/use-page-meta";

type DemoStep = "intro" | "config" | "voice-call" | "complete";

const Demo = () => {
  usePageMeta({
    title: "Live Demo",
    description: "Try MYNA AI's intelligent phone system live. Hear how our AI answers calls, books appointments, and handles customer inquiries for service businesses.",
    path: "/demo",
  });
  const [step, setStep] = useState<DemoStep>("intro");
  const [config, setConfig] = useState<DemoConfig | null>(null);

  // Generate unique session ID
  const sessionId = useMemo(() => {
    return `demo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }, []);

  const handleConfigSubmit = (formData: DemoConfig) => {
    setConfig(formData);
    setStep("voice-call");
  };

  const handleCallComplete = () => {
    setStep("complete");
  };

  const handleStartOver = () => {
    setStep("intro");
    setConfig(null);
  };

  return (
    <div className="min-h-screen bg-[var(--m-bg)]">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <AnimatePresence mode="wait">
            {/* STEP: INTRO */}
            {step === "intro" && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="text-center"
              >
                {/* Hero badge - matching homepage style */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="inline-flex items-center gap-2 bg-[var(--m-badge-bg)] border border-[var(--m-badge-border)] px-4 py-2 rounded-sm mb-8"
                >
                  <Sparkles className="w-4 h-4 text-lime-500" />
                  <span className="text-sm font-semibold text-lime-500">
                    Try It Now — No Account Required
                  </span>
                </motion.div>

                {/* Headline - larger, bolder */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-[var(--m-heading)] mb-6 tracking-tight"
                >
                  Experience Your
                  <span className="block mt-2 bg-gradient-to-r from-[#f4f4f4] to-lime-400 bg-clip-text text-transparent">
                    AI Receptionist Live
                  </span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-xl text-[var(--m-muted)] max-w-3xl mx-auto mb-12"
                >
                  Talk to a personalized AI receptionist customized for your business.
                  No phone calls needed — everything happens right in your browser.
                </motion.p>

                {/* Stats row - matching homepage differentiators */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-10"
                >
                  <div className="bg-[var(--m-elevated)] border border-[var(--m-border)] p-4 text-center">
                    <div className="text-3xl sm:text-4xl font-black text-[var(--m-lime)] mb-1">5 min</div>
                    <div className="text-xs text-[var(--m-subtle)] uppercase tracking-wider">Demo Length</div>
                  </div>
                  <div className="bg-[var(--m-elevated)] border border-[var(--m-border)] p-4 text-center">
                    <div className="text-3xl sm:text-4xl font-black text-[var(--m-heading)] mb-1">100%</div>
                    <div className="text-xs text-[var(--m-subtle)] uppercase tracking-wider">Personalized</div>
                  </div>
                  <div className="bg-[var(--m-elevated)] border border-[var(--m-border)] p-4 text-center">
                    <div className="text-3xl sm:text-4xl font-black text-[var(--m-lime)] mb-1">&lt;2s</div>
                    <div className="text-xs text-[var(--m-subtle)] uppercase tracking-wider">Response</div>
                  </div>
                </motion.div>

                {/* Demo experience card - enhanced styling */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mb-12 max-w-3xl mx-auto"
                >
                  <div className="bg-gradient-to-br from-[var(--m-bg-gradient-from)] via-[var(--m-card-alt)] to-[var(--m-card)] border border-[var(--m-border-strong)] p-8">
                    <div className="flex items-center justify-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-lime-400 to-lime-600 flex items-center justify-center shadow-2xl shadow-lime-500/20">
                        <Phone className="w-8 h-8 text-[#292929]" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-[var(--m-heading)] mb-4">
                      Experience AI Call Handling
                    </h3>
                    <p className="text-[var(--m-muted)] text-lg mb-8">
                      Call in as a customer and experience how the AI handles service
                      inquiries, schedules appointments, and captures leads.
                    </p>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="flex items-start gap-3 p-4 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                        <CheckCircle2 className="w-5 h-5 text-[var(--m-lime)] shrink-0 mt-0.5" />
                        <span className="text-sm text-[var(--m-muted)]">Professional greeting with your business name</span>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                        <Calendar className="w-5 h-5 text-lime-300 shrink-0 mt-0.5" />
                        <span className="text-sm text-[var(--m-muted)]">Real-time calendar availability check</span>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                        <Mic className="w-5 h-5 text-[var(--m-lime)] shrink-0 mt-0.5" />
                        <span className="text-sm text-[var(--m-muted)]">Natural conversation handling</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* CTA - enhanced styling */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    onClick={() => setStep("config")}
                    className="bg-gradient-to-r from-[#f4f4f4] to-lime-400 text-[#292929] font-black text-lg px-10 py-6 h-auto hover:opacity-90 shadow-2xl shadow-lime-500/25 gap-3"
                  >
                    <Mic className="w-5 h-5" />
                    Start Your Demo
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </motion.div>

                {/* Trust indicators - updated styling */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="flex flex-wrap items-center justify-center gap-8 mt-10 text-sm text-[var(--m-subtle)]"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-lime-400/70" />
                    <span>5-minute demo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-lime-300/70" />
                    <span>Microphone required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-lime-400/70" />
                    <span>3 demos per day</span>
                  </div>
                </motion.div>

                {/* Already convinced? */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mt-12 text-[var(--m-subtle)]"
                >
                  Already convinced?{" "}
                  <Link to="/request-demo" className="text-lime-500 hover:text-lime-400 font-semibold">
                    Book a Discovery Call →
                  </Link>
                </motion.p>
              </motion.div>
            )}

            {/* STEP: CONFIG */}
            {step === "config" && (
              <motion.div
                key="config"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <DemoConfigForm
                  onSubmit={handleConfigSubmit}
                  onBack={() => setStep("intro")}
                />
              </motion.div>
            )}

            {/* STEP: VOICE CALL */}
            {step === "voice-call" && config && (
              <motion.div
                key="voice-call"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <VoiceCallInterface
                  sessionId={sessionId}
                  config={config}
                  onComplete={handleCallComplete}
                  onBack={() => setStep("config")}
                />
              </motion.div>
            )}

            {/* STEP: COMPLETE */}
            {step === "complete" && (
              <motion.div
                key="complete"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="max-w-2xl mx-auto text-center py-12"
              >
                {/* Post-Call Reflection */}
                <div className="mb-8">
                  <PostCallReflection businessName={config?.businessName || "your business"} />
                </div>

                {/* Success badge */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="inline-flex items-center gap-2 bg-[var(--m-badge-bg)] border border-[var(--m-ring-border)] px-4 py-2 rounded-sm mb-6"
                >
                  <CheckCircle2 className="w-4 h-4 text-[var(--m-lime)]" />
                  <span className="text-sm font-semibold text-[var(--m-lime)]">Demo Complete</span>
                </motion.div>

                <div className="bg-gradient-to-br from-[var(--m-bg-gradient-from)] via-[var(--m-card-alt)] to-[var(--m-card)] border border-[var(--m-border-strong)] p-8 sm:p-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-lime-300 to-lime-500 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-lime-500/20">
                    <Sparkles className="w-10 h-10 text-[#292929]" />
                  </div>

                  <h2 className="text-3xl sm:text-4xl font-black text-[var(--m-heading)] mb-4 tracking-tight">
                    Thanks for Trying Myna!
                  </h2>

                  <p className="text-lg text-[var(--m-muted)] mb-10 max-w-lg mx-auto">
                    You just experienced what 24/7 AI-powered customer
                    service can do for your business. Imagine never missing another
                    call.
                  </p>

                  <div className="grid sm:grid-cols-3 gap-4 mb-10">
                    <div className="p-4 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                      <div className="text-3xl font-black text-[var(--m-lime)] mb-1">24/7</div>
                      <div className="text-xs text-[var(--m-subtle)] uppercase tracking-wider">
                        Always Available
                      </div>
                    </div>
                    <div className="p-4 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                      <div className="text-3xl font-black text-[var(--m-heading)] mb-1">100%</div>
                      <div className="text-xs text-[var(--m-subtle)] uppercase tracking-wider">
                        Calls Answered
                      </div>
                    </div>
                    <div className="p-4 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                      <div className="text-3xl font-black text-[var(--m-lime)] mb-1">30%+</div>
                      <div className="text-xs text-[var(--m-subtle)] uppercase tracking-wider">
                        More Bookings
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      asChild
                      className="bg-gradient-to-r from-[#f4f4f4] to-lime-500 text-[#292929] font-bold hover:opacity-90 shadow-lg shadow-lime-500/20 gap-2"
                    >
                      <Link to="/request-demo">
                        <Sparkles className="w-5 h-5" />
                        Book a Discovery Call
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleStartOver}
                      className="border-white/[0.15] hover:bg-white/[0.05] text-[var(--m-muted)]"
                    >
                      Try Another Demo
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Demo;
